import { useEffect, useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import "@/hero.css";

const EASE = [0.16, 1, 0.3, 1];
const rise = (delay, y) => ({ hidden: { opacity: 0, y }, show: { opacity: 1, y: 0, transition: { duration: 1.15, delay, ease: EASE } } });
const bloom = (delay, rot) => ({ hidden: { opacity: 0, scale: 0.55, rotate: rot - 7 }, show: { opacity: 1, scale: 1, rotate: rot, transition: { duration: 1.05, delay, ease: EASE } } });
const glide = (delay, x, y, rot) => ({ hidden: { opacity: 0, x, y, rotate: rot - 9 }, show: { opacity: 1, x: 0, y: 0, rotate: rot, transition: { duration: 1.1, delay, ease: EASE } } });
const land = (delay, rot) => ({ hidden: { opacity: 0, x: 34, y: -46, scale: 1.18, rotate: rot + 14 }, show: { opacity: 1, x: 0, y: 0, scale: 1, rotate: rot, transition: { duration: 0.95, delay, ease: EASE } } });
const fadeUp = (delay, y = 14) => ({ hidden: { opacity: 0, y }, show: { opacity: 1, y: 0, transition: { duration: 0.85, delay, ease: EASE } } });

const motes = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: 8 + ((i * 79) % 84),
  top: 6 + ((i * 43) % 88),
  size: 2 + (i % 3),
  delay: 2.05 + (i % 7) * 0.07,
  dur: 8 + (i % 6),
}));

const botanicals = [
  { key: "anemone", src: "/assets/flower-anemone.png", cls: "piece-anemone", variants: bloom(1.18, -13), depth: "flower", float: 9 },
  { key: "ranunculus", src: "/assets/flower-ranunculus.png", cls: "piece-ranunculus", variants: bloom(1.4, 9), depth: "flower", float: 11 },
  { key: "dahlia", src: "/assets/flower.png", cls: "piece-dahlia", variants: bloom(1.6, -16), depth: "flower", float: 13 },
  { key: "fern", src: "/assets/leaf-fern.png", cls: "piece-fern", variants: glide(1.52, -40, -18, 26), depth: "leaf", float: 12 },
  { key: "eucalyptus", src: "/assets/leaf-eucalyptus.png", cls: "piece-eucalyptus", variants: glide(1.7, 38, 20, -124), depth: "leaf", float: 10 },
  { key: "fern-low", src: "/assets/leaf-fern.png", cls: "piece-fern-low", variants: glide(1.82, -26, 26, 168), depth: "leaf", float: 14 },
];

const Piece = ({ scrollShift, parallax, variants, cls, src, float, testid, reduced, children }) => (
  <motion.div className={`piece ${cls}`} style={{ y: scrollShift }}>
    <motion.div className="piece-parallax" style={parallax}>
      <motion.div className="piece-inner" variants={variants} initial={reduced ? "show" : "hidden"} animate="show" data-testid={testid}>
        {children || (
          <span className="art-wrap" style={{ animationDuration: `${float}s`, animationDelay: `${float / 3}s`, "--art": `url(${src})` }}>
            <img className="piece-art" src={src} alt="" />
          </span>
        )}
      </motion.div>
    </motion.div>
  </motion.div>
);

export const HeroMonogram = ({ monogram = "SS", plateName = "Satyam" }) => {
  const heroRef = useRef(null);
  const reduced = useReducedMotion();
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const deepX = useSpring(pointerX, { stiffness: 44, damping: 25, mass: 0.95 });
  const deepY = useSpring(pointerY, { stiffness: 44, damping: 25, mass: 0.95 });
  const midX = useSpring(pointerX, { stiffness: 62, damping: 21, mass: 0.72 });
  const midY = useSpring(pointerY, { stiffness: 62, damping: 21, mass: 0.72 });
  const nearX = useSpring(pointerX, { stiffness: 84, damping: 18, mass: 0.5 });
  const nearY2 = useSpring(pointerY, { stiffness: 84, damping: 18, mass: 0.5 });

  const paper = { x: useTransform(deepX, (v) => v * 5), y: useTransform(deepY, (v) => v * 5) };
  const frame = { x: useTransform(deepX, (v) => v * 8), y: useTransform(deepY, (v) => v * 8) };
  const flower = { x: useTransform(midX, (v) => v * 12), y: useTransform(midY, (v) => v * 12) };
  const leaf = { x: useTransform(midX, (v) => v * 15), y: useTransform(midY, (v) => v * 15) };
  const wing = { x: useTransform(nearX, (v) => v * 20), y: useTransform(nearY2, (v) => v * 20) };
  const tiltY = useTransform(deepX, (v) => v * 1.6);
  const tiltX = useTransform(deepY, (v) => v * -1.2);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const stageScale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);
  const paperY = useTransform(scrollYProgress, [0, 1], ["0%", "6%"]);
  const frameY = useTransform(scrollYProgress, [0, 1], [0, 24]);
  const nearY = useTransform(scrollYProgress, [0, 1], [0, -64]);
  const midShift = useTransform(scrollYProgress, [0, 1], [0, -30]);
  const veilOpacity = useTransform(scrollYProgress, [0, 0.7], [0, 0.55]);

  useEffect(() => {
    if (reduced) return;
    const onMove = (event) => {
      pointerX.set((event.clientX / window.innerWidth) * 2 - 1);
      pointerY.set((event.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [reduced, pointerX, pointerY]);

  return (
    <section className="hero-stage" ref={heroRef} data-testid="hero-section">
      <motion.div className="hero-paper" style={{ y: paperY, x: paper.x }} variants={fadeUp(0, 0)} initial={reduced ? "show" : "hidden"} animate="show" />
      <motion.div className="hero-veil" style={{ opacity: veilOpacity }} aria-hidden="true" />

      <motion.div className="hero-composition" style={{ scale: stageScale }} initial={reduced ? "show" : "hidden"} animate="show" data-testid="hero-composition">
        <div className="composition-box">
          <Piece scrollShift={midShift} parallax={flower} reduced={reduced} variants={botanicals[2].variants} cls={botanicals[2].cls} src={botanicals[2].src} float={botanicals[2].float} testid="hero-flower-dahlia" />
          <Piece scrollShift={midShift} parallax={leaf} reduced={reduced} variants={botanicals[3].variants} cls={botanicals[3].cls} src={botanicals[3].src} float={botanicals[3].float} testid="hero-leaf-fern" />

          <Piece scrollShift={frameY} parallax={frame} reduced={reduced} variants={rise(0.34, 74)} cls="piece-frame" testid="hero-frame">
            <motion.div className="plate-mount" style={{ rotate: -1.1, rotateX: tiltX, rotateY: tiltY }}>
              <div className="plate">
                <motion.span
                  className="monogram"
                  data-testid="hero-monogram-letters"
                  initial={reduced ? "show" : "hidden"}
                  animate="show"
                  variants={{ hidden: { clipPath: "inset(0% 0% 100% 0%)", y: 16, opacity: 0.2 }, show: { clipPath: "inset(0% 0% 0% 0%)", y: 0, opacity: 1, transition: { duration: 0.95, delay: 0.92, ease: EASE } } }}
                >
                  {monogram}
                </motion.span>
                <motion.span className="plate-name" variants={fadeUp(1.55, 8)} initial={reduced ? "show" : "hidden"} animate="show" data-testid="hero-plate-name">{plateName}</motion.span>
                <span className="plate-rule" />
              </div>
            </motion.div>
          </Piece>

          <Piece scrollShift={nearY} parallax={flower} reduced={reduced} variants={botanicals[0].variants} cls={botanicals[0].cls} src={botanicals[0].src} float={botanicals[0].float} testid="hero-flower-anemone" />
          <Piece scrollShift={nearY} parallax={flower} reduced={reduced} variants={botanicals[1].variants} cls={botanicals[1].cls} src={botanicals[1].src} float={botanicals[1].float} testid="hero-flower-ranunculus" />
          <Piece scrollShift={nearY} parallax={leaf} reduced={reduced} variants={botanicals[4].variants} cls={botanicals[4].cls} src={botanicals[4].src} float={botanicals[4].float} testid="hero-leaf-eucalyptus" />
          <Piece scrollShift={nearY} parallax={leaf} reduced={reduced} variants={botanicals[5].variants} cls={botanicals[5].cls} src={botanicals[5].src} float={botanicals[5].float} testid="hero-leaf-fern-low" />

          <Piece scrollShift={nearY} parallax={wing} reduced={reduced} variants={land(1.94, -7)} cls="piece-moth" testid="hero-moth">
            <div className="moth" aria-hidden="true">
              <img className="moth-wing moth-left" src="/assets/moth-left.png" alt="" />
              <img className="moth-wing moth-right" src="/assets/moth-right.png" alt="" />
            </div>
          </Piece>

          <Piece scrollShift={nearY} parallax={wing} reduced={reduced} variants={fadeUp(2.12, 10)} cls="piece-beetle" src="/assets/beetle.png" float={16} testid="hero-beetle" />

          <motion.div className="motes" variants={fadeUp(2.1, 0)} initial={reduced ? "show" : "hidden"} animate="show" aria-hidden="true">
            {motes.map((m) => (
              <span key={m.id} className="mote" style={{ left: `${m.left}%`, top: `${m.top}%`, width: m.size, height: m.size, animationDuration: `${m.dur}s`, animationDelay: `${m.delay}s` }} />
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.a href="#work" className="hero-arrow" initial={reduced ? "show" : "hidden"} animate="show" variants={fadeUp(2.62, 10)} data-testid="hero-scroll-link">
        <span className="arrow-line" />
        <svg width="22" height="12" viewBox="0 0 22 12" fill="none" aria-hidden="true"><path d="M1 1l10 10L21 1" stroke="currentColor" strokeWidth="1" /></svg>
      </motion.a>
    </section>
  );
};
